<template>
  <div class="user-admin-panel" v-loading="busy">
    <section class="panel-header">
      <div>
        <h3>用户密码管理</h3>
        <p>SSVIP</p>
      </div>
      <el-tag effect="plain">用户配置</el-tag>
    </section>

    <el-tabs v-model="activeTab" class="admin-tabs">
      <el-tab-pane label="修改密码" name="password">
        <el-form
          ref="passwordFormRef"
          :model="passwordForm"
          :rules="passwordRules"
          label-position="top"
          class="form-surface"
        >
          <el-form-item label="目标用户名" prop="username">
            <el-input
              v-model.trim="passwordForm.username"
              :prefix-icon="User"
              clearable
              autocomplete="off"
              @keyup.enter="handlePasswordSubmit"
            />
          </el-form-item>

          <div class="form-grid">
            <el-form-item label="新密码" prop="newPassword">
              <el-input
                v-model="passwordForm.newPassword"
                :prefix-icon="Lock"
                type="password"
                show-password
                autocomplete="new-password"
                @keyup.enter="handlePasswordSubmit"
              />
            </el-form-item>

            <el-form-item label="确认新密码" prop="confirmPassword">
              <el-input
                v-model="passwordForm.confirmPassword"
                :prefix-icon="Lock"
                type="password"
                show-password
                autocomplete="new-password"
                @keyup.enter="handlePasswordSubmit"
              />
            </el-form-item>
          </div>

          <div class="form-actions">
            <el-button :icon="RefreshLeft" plain :disabled="busy" @click="resetPasswordForm">
              清空
            </el-button>
            <el-button type="primary" :icon="Key" :loading="busy" @click="handlePasswordSubmit">
              修改密码
            </el-button>
          </div>
        </el-form>
      </el-tab-pane>

      <el-tab-pane label="注册用户 / 用户管理" name="users">
        <div class="user-management">
          <el-form
            ref="userFormRef"
            :model="userForm"
            :rules="userRules"
            label-position="top"
            class="form-surface"
          >
            <div class="form-grid">
              <el-form-item label="用户名" prop="username">
                <el-input
                  v-model.trim="userForm.username"
                  :prefix-icon="User"
                  :disabled="editMode"
                  clearable
                  autocomplete="off"
                />
              </el-form-item>

              <el-form-item label="岗位" prop="job">
                <el-select v-model="userForm.job">
                  <el-option v-for="job in jobOptions" :key="job" :label="job" :value="job" />
                </el-select>
              </el-form-item>
            </div>

            <div class="form-grid">
              <el-form-item v-if="!editMode" label="初始密码" prop="password">
                <el-input
                  v-model="userForm.password"
                  :prefix-icon="Lock"
                  type="password"
                  show-password
                  autocomplete="new-password"
                />
              </el-form-item>

              <el-form-item v-if="!editMode" label="确认密码" prop="confirmPassword">
                <el-input
                  v-model="userForm.confirmPassword"
                  :prefix-icon="Lock"
                  type="password"
                  show-password
                  autocomplete="new-password"
                />
              </el-form-item>
            </div>

            <div class="form-grid">
              <el-form-item label="用户等级" prop="webstatus">
                <el-select v-model="userForm.webstatus">
                  <el-option v-for="status in webstatusOptions" :key="status" :label="status" :value="status" />
                </el-select>
              </el-form-item>

              <el-form-item label="权限模板" prop="permissionTemplates">
                <el-select
                  v-model="userForm.permissionTemplates"
                  multiple
                  collapse-tags
                  collapse-tags-tooltip
                  @change="handleTemplateChange"
                >
                  <el-option
                    v-for="template in permissionTemplateOptions"
                    :key="template"
                    :label="template"
                    :value="template"
                  />
                </el-select>
              </el-form-item>
            </div>

            <el-form-item label="负责车型" prop="responsibleModels">
              <el-select
                v-model="userForm.responsibleModels"
                multiple
                collapse-tags
                collapse-tags-tooltip
              >
                <el-option v-for="model in modelOptions" :key="model" :label="model" :value="model" />
              </el-select>
            </el-form-item>

            <el-form-item label="账号状态">
              <el-switch
                v-model="userForm.enabled"
                active-text="启用"
                inactive-text="禁用"
              />
            </el-form-item>

            <div class="form-actions">
              <el-button :icon="RefreshLeft" plain :disabled="busy" @click="resetUserForm">
                清空
              </el-button>
              <el-button type="primary" :icon="Check" :loading="busy" @click="handleUserSubmit">
                {{ editMode ? '保存修改' : '保存用户' }}
              </el-button>
            </div>
          </el-form>

          <section class="user-table-wrap">
            <div class="table-toolbar">
              <strong>用户列表</strong>
              <el-button :icon="Refresh" plain size="small" :loading="loadingUsers" @click="loadUsers">
                刷新
              </el-button>
            </div>
            <el-table :data="users" size="small" border>
              <el-table-column prop="username" label="用户名" min-width="120" />
              <el-table-column label="等级" min-width="90">
                <template #default="{ row }">
                  {{ row.userInfo?.webstatus || row.role || 'USER' }}
                </template>
              </el-table-column>
              <el-table-column label="权限模板" min-width="150">
                <template #default="{ row }">
                  <div class="tag-list">
                    <el-tag
                      v-for="template in formatTemplates(row)"
                      :key="template"
                      size="small"
                      :type="template === 'SGM' ? 'success' : 'info'"
                      effect="plain"
                    >
                      {{ template }}
                    </el-tag>
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="负责车型" min-width="180">
                <template #default="{ row }">
                  <span>{{ formatModels(row.userInfo?.responsibleModels) }}</span>
                </template>
              </el-table-column>
              <el-table-column label="状态" width="82" align="center">
                <template #default="{ row }">
                  <el-tag size="small" :type="row.enabled === false ? 'danger' : 'success'" effect="plain">
                    {{ row.enabled === false ? '禁用' : '启用' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="IP白名单" width="104" align="center">
                <template #default="{ row }">
                  <el-tag
                    size="small"
                    :type="row.userInfo?.loginIpWhitelist?.enabled ? 'warning' : 'info'"
                    effect="plain"
                  >
                    {{ whitelistStateText(row.userInfo?.loginIpWhitelist) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="96" align="center">
                <template #default="{ row }">
                  <el-button text type="primary" size="small" @click="editUser(row)">
                    编辑
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </section>
        </div>
      </el-tab-pane>

      <el-tab-pane label="登录IP白名单" name="loginIpWhitelist">
        <div class="ip-admin">
          <el-form label-position="top" class="form-surface">
            <div class="form-grid">
              <el-form-item label="用户">
                <el-select
                  v-model="whitelistForm.username"
                  filterable
                  clearable
                  placeholder="请选择用户"
                  @change="handleWhitelistUserChange"
                >
                  <el-option
                    v-for="user in users"
                    :key="user.username"
                    :label="user.username"
                    :value="user.username"
                  />
                </el-select>
              </el-form-item>

              <el-form-item label="状态">
                <el-switch
                  v-model="whitelistForm.enabled"
                  active-text="启用"
                  inactive-text="关闭"
                />
              </el-form-item>
            </div>

            <el-form-item label="白名单 IP">
              <el-input
                v-model="whitelistForm.ipsText"
                type="textarea"
                :rows="5"
                placeholder="每行一个 IPv4 或 CIDR，例如 1.2.3.4 或 10.0.0.0/24"
              />
            </el-form-item>

            <el-form-item label="备注">
              <el-input v-model.trim="whitelistForm.remark" clearable />
            </el-form-item>

            <div class="form-actions">
              <el-button :icon="Refresh" plain :loading="loadingUsers" @click="loadUsers">
                刷新用户
              </el-button>
              <el-button type="primary" :icon="Check" :loading="saving" @click="saveWhitelist">
                保存白名单
              </el-button>
            </div>
          </el-form>

          <section class="user-table-wrap">
            <div class="table-toolbar">
              <strong>访问IP日志</strong>
              <el-button :icon="Refresh" plain size="small" :loading="loadingIpLogs" @click="loadIpLogs">
                刷新
              </el-button>
            </div>
            <div class="form-grid log-filter-grid">
              <el-input v-model.trim="logQueryForm.username" placeholder="用户名" clearable />
              <el-input v-model.trim="logQueryForm.ip" placeholder="IP 关键字" clearable />
              <el-select v-model="logQueryForm.eventType" clearable placeholder="事件类型">
                <el-option label="登录成功" value="login_success" />
                <el-option label="登录失败" value="login_failed" />
                <el-option label="登录拦截" value="login_blocked" />
                <el-option label="Token过滤访问" value="token_filter_access" />
                <el-option label="后台访问" value="admin_api_access" />
              </el-select>
              <el-input-number v-model="logQueryForm.limit" :min="1" :max="500" />
            </div>
            <el-table :data="ipLogs" size="small" border>
              <el-table-column prop="createdAt" label="时间" min-width="150" />
              <el-table-column prop="username" label="用户" min-width="110" />
              <el-table-column prop="eventType" label="事件" min-width="120" />
              <el-table-column prop="clientIp" label="判断IP" min-width="130" />
              <el-table-column prop="xForwardedFor" label="X-Forwarded-For" min-width="220" show-overflow-tooltip />
              <el-table-column prop="xRealIp" label="X-Real-IP" min-width="130" />
              <el-table-column prop="peerIp" label="直连IP" min-width="130" />
              <el-table-column prop="path" label="路径" min-width="220" show-overflow-tooltip />
              <el-table-column prop="result" label="结果" min-width="90" />
              <el-table-column prop="reason" label="原因" min-width="160" show-overflow-tooltip />
            </el-table>
          </section>
        </div>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { Check, Key, Lock, Refresh, RefreshLeft, User } from '@element-plus/icons-vue';

import {
  adminCreateUser,
  adminGetUserIpAccessLogs,
  adminListUsers,
  adminResetUserPassword,
  adminUpdateUser,
  adminUpdateUserLoginIpWhitelist
} from '@/api/users';
import {
  buildAccessLogParams,
  parseWhitelistText,
  whitelistStateText,
  whitelistToText
} from '@/utils/loginIpWhitelist.mjs';

const activeTab = ref('password');
const passwordFormRef = ref(null);
const userFormRef = ref(null);
const saving = ref(false);
const loadingUsers = ref(false);
const loadingIpLogs = ref(false);
const users = ref([]);
const ipLogs = ref([]);
const editMode = ref(false);

const busy = computed(() => saving.value || loadingUsers.value);

const passwordForm = reactive({
  username: '',
  newPassword: '',
  confirmPassword: '',
});

const jobOptions = ['开发', '测试', '管理员'];
const webstatusOptions = ['USER', 'VIP', 'SVIP', 'SSVIP'];
const permissionTemplateOptions = ['FORD', 'MAZDA', 'SGM'];
const modelOptions = ['FORD', 'MAZDA', 'SGM'];

const emptyUserForm = () => ({
  username: '',
  password: '',
  confirmPassword: '',
  job: '测试',
  webstatus: 'VIP',
  permissionTemplates: ['FORD'],
  responsibleModels: [],
  enabled: true,
});

const userForm = reactive(emptyUserForm());

const whitelistForm = reactive({
  username: '',
  enabled: false,
  ipsText: '',
  remark: '',
});

const logQueryForm = reactive({
  username: '',
  ip: '',
  eventType: '',
  limit: 100,
});

const validatePassword = (rule, value, callback) => {
  if (!value) {
    callback(new Error('请输入密码'));
    return;
  }
  if (value.length < 6) {
    callback(new Error('密码长度不能少于 6 位'));
    return;
  }
  if (!/[A-Za-z]/.test(value) || !/\d/.test(value)) {
    callback(new Error('密码必须包含字母和数字'));
    return;
  }
  callback();
};

const validatePasswordConfirm = (sourceKey) => (rule, value, callback) => {
  if (!value) {
    callback(new Error('请确认密码'));
    return;
  }
  if (value !== (sourceKey === 'newPassword' ? passwordForm.newPassword : userForm.password)) {
    callback(new Error('两次输入的密码不一致'));
    return;
  }
  callback();
};

const passwordRules = {
  username: [{ required: true, message: '请输入目标用户名', trigger: 'blur' }],
  newPassword: [{ validator: validatePassword, trigger: 'blur' }],
  confirmPassword: [{ validator: validatePasswordConfirm('newPassword'), trigger: 'blur' }],
};

const userRules = computed(() => ({
  username: [{ required: true, message: '请输入用户名', trigger: 'blur' }],
  password: editMode.value ? [] : [{ validator: validatePassword, trigger: 'blur' }],
  confirmPassword: editMode.value ? [] : [{ validator: validatePasswordConfirm('password'), trigger: 'blur' }],
  job: [{ required: true, message: '请选择岗位', trigger: 'change' }],
  webstatus: [{ required: true, message: '请选择用户等级', trigger: 'change' }],
  permissionTemplates: [{ type: 'array', required: true, min: 1, message: '请选择权限模板', trigger: 'change' }],
  responsibleModels: [{ type: 'array', required: true, min: 1, message: '请选择负责车型', trigger: 'change' }],
}));

const errorMessage = (error) => {
  if (error?.status === 401) return '登录已失效';
  if (error?.status === 403) return '当前账号没有用户管理权限';
  if (error?.status === 404) return '目标用户不存在';
  if (error?.status === 409) return '用户名已存在';
  if (error?.status === 400) return error.info || error.message || '参数不符合要求';
  return '操作失败';
};

const resetPasswordForm = () => {
  passwordForm.newPassword = '';
  passwordForm.confirmPassword = '';
  passwordFormRef.value?.clearValidate();
};

const resetUserForm = () => {
  Object.assign(userForm, emptyUserForm());
  editMode.value = false;
  userFormRef.value?.clearValidate();
};

const handleTemplateChange = (value) => {
  if (Array.isArray(value) && value.length === 1 && value[0] === 'SGM') {
    userForm.responsibleModels = ['SGM'];
  } else if (Array.isArray(value) && value.includes('SGM') && !userForm.responsibleModels.includes('SGM')) {
    userForm.responsibleModels = [...userForm.responsibleModels, 'SGM'];
  }
};

const formatModels = (models) => {
  return Array.isArray(models) && models.length ? models.join('、') : '-';
};

const formatTemplates = (row) => {
  const templates = row?.permissionTemplates || row?.userInfo?.permissionTemplates;
  if (Array.isArray(templates) && templates.length) {
    return templates.filter((item) => permissionTemplateOptions.includes(item));
  }

  const legacy = row?.permissionTemplate || row?.userInfo?.permissionTemplate;
  return permissionTemplateOptions.includes(legacy) ? [legacy] : ['FORD'];
};

const normalizeUserResponse = (response) => {
  return response?.data?.data?.users || response?.data?.users || [];
};

const loadUsers = async () => {
  loadingUsers.value = true;
  try {
    const response = await adminListUsers();
    users.value = normalizeUserResponse(response);
  } catch (error) {
    ElMessage.error(errorMessage(error));
  } finally {
    loadingUsers.value = false;
  }
};

const fillWhitelistForm = (row) => {
  const whitelist = row?.userInfo?.loginIpWhitelist || {};
  whitelistForm.username = row?.username || row?.userInfo?.username || '';
  whitelistForm.enabled = whitelist.enabled === true;
  whitelistForm.ipsText = whitelistToText(whitelist);
  whitelistForm.remark = whitelist.remark || '';
};

const handleWhitelistUserChange = (username) => {
  const row = users.value.find((item) => item.username === username);
  if (row) {
    fillWhitelistForm(row);
  }
};

const saveWhitelist = async () => {
  if (!whitelistForm.username) {
    ElMessage.warning('请选择用户');
    return;
  }

  const ips = parseWhitelistText(whitelistForm.ipsText);
  if (whitelistForm.enabled && ips.length === 0) {
    ElMessage.warning('启用白名单时至少填写一个 IP');
    return;
  }

  saving.value = true;
  try {
    await adminUpdateUserLoginIpWhitelist(whitelistForm.username, {
      enabled: whitelistForm.enabled,
      ips,
      remark: whitelistForm.remark,
    });
    ElMessage.success('登录IP白名单已保存');
    await loadUsers();
    handleWhitelistUserChange(whitelistForm.username);
  } catch (error) {
    ElMessage.error(errorMessage(error));
  } finally {
    saving.value = false;
  }
};

const loadIpLogs = async () => {
  loadingIpLogs.value = true;
  try {
    const response = await adminGetUserIpAccessLogs(buildAccessLogParams(logQueryForm));
    ipLogs.value = response?.data?.data?.logs || response?.data?.logs || [];
  } catch (error) {
    ElMessage.error(errorMessage(error));
  } finally {
    loadingIpLogs.value = false;
  }
};

const handlePasswordSubmit = async () => {
  if (!passwordFormRef.value) return;
  const valid = await passwordFormRef.value.validate().catch(() => false);
  if (!valid) return;

  saving.value = true;
  try {
    await adminResetUserPassword({
      username: passwordForm.username,
      newPassword: passwordForm.newPassword,
    });
    ElMessage.success('密码修改成功');
    resetPasswordForm();
  } catch (error) {
    ElMessage.error(errorMessage(error));
  } finally {
    saving.value = false;
  }
};

const compatibilityTemplate = () => (
  userForm.permissionTemplates.length === 1 ? userForm.permissionTemplates[0] : 'MULTI'
);

const userPayload = () => ({
  username: userForm.username,
  password: userForm.password,
  confirmPassword: userForm.confirmPassword,
  userInfo: {
    username: userForm.username,
    job: userForm.job,
    webstatus: userForm.webstatus,
    responsibleModels: userForm.responsibleModels,
    permissionTemplates: userForm.permissionTemplates,
    permissionTemplate: compatibilityTemplate(),
    enabled: userForm.enabled,
  },
});

const handleUserSubmit = async () => {
  if (!userFormRef.value) return;
  const valid = await userFormRef.value.validate().catch(() => false);
  if (!valid) return;

  saving.value = true;
  try {
    const payload = userPayload();
    if (editMode.value) {
      delete payload.password;
      delete payload.confirmPassword;
      await adminUpdateUser(userForm.username, payload);
      ElMessage.success('用户信息已保存');
    } else {
      await adminCreateUser(payload);
      ElMessage.success('用户创建成功');
    }
    resetUserForm();
    await loadUsers();
  } catch (error) {
    ElMessage.error(errorMessage(error));
  } finally {
    saving.value = false;
  }
};

const editUser = (row) => {
  const info = row.userInfo || {};
  editMode.value = true;
  userForm.username = row.username || info.username || '';
  userForm.password = '';
  userForm.confirmPassword = '';
  userForm.job = jobOptions.includes(info.job) ? info.job : '测试';
  userForm.webstatus = webstatusOptions.includes(info.webstatus) ? info.webstatus : 'VIP';
  userForm.permissionTemplates = formatTemplates(row);
  userForm.responsibleModels = Array.isArray(info.responsibleModels)
    ? info.responsibleModels.filter((item) => modelOptions.includes(item))
    : [];
  userForm.enabled = row.enabled !== false;
  activeTab.value = 'users';
  userFormRef.value?.clearValidate();
};

watch(activeTab, (value) => {
  if (value === 'users' && users.value.length === 0) {
    loadUsers();
  }
  if (value === 'loginIpWhitelist') {
    if (users.value.length === 0) {
      loadUsers();
    }
    if (ipLogs.value.length === 0) {
      loadIpLogs();
    }
  }
});

onMounted(() => {
  loadUsers();
});
</script>

<style scoped>
.user-admin-panel {
  display: grid;
  gap: 14px;
  min-width: 0;
}

.panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border: 1px solid #dfe8f3;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 8px 20px rgba(31, 56, 88, 0.05);
}

.panel-header h3 {
  margin: 0;
  color: #142134;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0;
}

.panel-header p {
  margin: 5px 0 0;
  color: #64748b;
  font-size: 12px;
  line-height: 1.4;
}

.admin-tabs {
  min-width: 0;
}

.form-surface,
.user-table-wrap {
  padding: 18px;
  border: 1px solid #dfe8f3;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 8px 20px rgba(31, 56, 88, 0.05);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 4px;
}

.user-management {
  display: grid;
  gap: 14px;
}

.ip-admin {
  display: grid;
  gap: 14px;
}

.log-filter-grid {
  margin-bottom: 12px;
}

.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.table-toolbar strong {
  color: #142134;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

:deep(.el-input__wrapper),
:deep(.el-select__wrapper) {
  border-radius: 8px;
}

@media (max-width: 760px) {
  .panel-header {
    align-items: stretch;
    flex-direction: column;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-actions {
    flex-direction: column-reverse;
  }

  .form-actions .el-button {
    width: 100%;
    margin-left: 0;
  }
}
</style>
